import React from 'react'

import spacetime from 'spacetime'

import { Box, Card, Flex, Text } from '@driftt/dds'

import { AccountActivityUserAvatar } from 'components/Avatars'
import { ActivityEndUser, ActivityEvent, ActivityType } from 'modules/accountActivity/domain'
import { EndUserDisplayInfo } from 'utils/endUser'

import { ActivityIcon } from './ActivityIcon'
import { ContentRecommentationContent } from './ContentRecommendationContent'
import { ConversationContent } from './ConversationContent'
import { EmailContent } from './EmailContent'
import { GenericContent } from './GenericContent'
import { PageViewContent } from './PageViewContent'
import { SearchContent } from './SearchContent'
import { VideoContent } from './VideoContent'

interface EndUserActivityProps {
  activityEvent: ActivityEvent
  endUserData: ActivityEndUser
  align?: 'left' | 'center'
  presentEndUserIds: Set<number>
  trackAction: (action: string, additionalData?: any) => void
}
export const EndUserActivity = ({
  activityEvent,
  endUserData,
  align = 'left',
  presentEndUserIds,
  trackAction
}: EndUserActivityProps) => {
  const timestamp = spacetime(activityEvent.createdAt).format('{hour}:{minute-pad} {AMPM}')
  const alignLeft = align === 'left'
  const ContentComponent = () => {
    switch (activityEvent.activityType) {
      case ActivityType.PAGE_VIEW:
        return (
          <PageViewContent
            pages={activityEvent.activityContext.pageVisits ?? []}
            trackAction={trackAction}
            referrer={activityEvent.activityContext.referrer}
          />
        )
      case ActivityType.CONVERSATION:
        return (
          <ConversationContent
            conversationPreview={activityEvent.activityContext.conversationPreview ?? ''}
            convoId={activityEvent.activityContext.id}
            endUser={endUserData}
            trackAction={trackAction}
          />
        )

      case ActivityType.VIDEO:
        return (
          <VideoContent
            url={activityEvent.activityContext.url}
            title={activityEvent.activityContext.title}
            thumbnailUrl={activityEvent.activityContext.thumbnailUrl}
            percentViewed={activityEvent.activityContext.percentViewed}
            trackAction={trackAction}
          />
        )
      case ActivityType.EMAIL_OPEN:
      case ActivityType.EMAIL_CLICK:
      case ActivityType.EMAIL_CLICK_SFDC:
      case ActivityType.EMAIL_OPEN_SFDC:
      case ActivityType.EMAIL_INBOUND:
        return (
          <EmailContent
            preview={activityEvent.activityContext.emailPreviewData}
            interactions={activityEvent.activityContext.emailInteractionData}
          />
        )
      case ActivityType.WIDGET_CONTENT_CLICK:
        return (
          <ContentRecommentationContent
            pages={[activityEvent.activityContext]}
            trackAction={trackAction}
          />
        )
      case ActivityType.WIDGET_SEARCH_SEND:
        return (
          <SearchContent
            searchQuery={activityEvent.activityContext.prompt || ''}
            pages={activityEvent.activityContext.pageVisits ?? []}
            trackAction={trackAction}
          />
        )
      case ActivityType.FILLED_OUT_FORM:
      case ActivityType.UNSUBSCRIBED:
      case ActivityType.END_USER_IDENTIFIED:
      case ActivityType.SMS_INBOUND:
      case ActivityType.OTHER_CHATBOT_CONVERSATION:
      case ActivityType.CONTRACT_REQUEST:
      case ActivityType.WEBINAR:
      case ActivityType.ADDED:
        return <GenericContent activityContext={activityEvent.activityContext} activityType={activityEvent.activityType} />
      default:
        return <></>
    }
  }
  return (
    <Box
      key={activityEvent.createdAt}
      css={{
        position: 'relative',
        zIndex: 1
      }}>
      <Text
        variant="metadata"
        css={{ position: 'relative', top: '-8px', left: alignLeft ? '-69%' : 'inherit' }}>
        {timestamp}
      </Text>
      {/* event card */}
      <Card
        css={{
          width: '270px',
          padding: '16px',
          marginBottom: '24px',
          position: 'relative',
          left: alignLeft ? '-70%' : 'inherit'
        }}>
        <Card.Header>
          <Flex alignItems="center">
            <AccountActivityUserAvatar
              imageUrl={endUserData.avatarUrl}
              name={endUserData.name}
              size="small"
              status={presentEndUserIds.has(endUserData.id) ? 'available' : 'away'}
            />
            <EndUserDisplayInfo
              endUserData={endUserData}
              orientation="vertical"
              trackContactClick={() =>
                trackAction('Contact click', { activityType: activityEvent.activityType })
              }
            />
          </Flex>
          <Card.Content>
            {/* activity event content */}
            <ContentComponent />
          </Card.Content>
        </Card.Header>
      </Card>
      {/* center icon on timeline */}
      <Flex
        fullWidth
        justifyContent="center"
        css={{
          position: 'relative',
          top: alignLeft ? '-50%' : 'calc(-100% + 32px)'
        }}>
        <ActivityIcon activityType={activityEvent.activityType} />
      </Flex>
      {/* horizontal line */}
      {alignLeft && (
        <Box
          css={{
            backgroundColor: '$gray4',
            height: '1px',
            position: 'relative',
            left: '-50%',
            top: 'calc(-50% - 12px)',
            zIndex: '-1'
          }}
        />
      )}
    </Box>
  )
}
